<template lang="pug">
uc-preload-sfx(name="complete")
uc-preload-sfx(name="click2")

span.uc-hotspot-bg.blur.absolute.h-full.w-full.top-0.left-0.z-0.pointer-events-none(
  :data-src="screen.img"
  v-html="svg"
  v-if="((isFullscreenHotspot && !largeDevice) || isHotspotGame) && !hideHotspotBg"
)

span.uc-hotspot.relative.z-0.w-full.block(
  :class="[$attrs.class, (!isFullscreenHotspot && !isHotspotGame) ? screen.styles?.img : undefined, { 'hidden': !largeDevice}, { 'fade-in': !isHotspotGame}]"
  :data-src="screen.img"
  data-sfx="click2"
  ref="elRef"
  v-html="svg"
)

.hotspot-tooltips.z-50.opacity-0(
  :class="[activeTooltip === getLightboxId(name) ? 'block' : 'hidden']"
  :data-tooltip-id="getLightboxId(name)"
  :id="getLightboxId(name)"
  :ref="setTooltipRef"
  tabindex="0"
  v-for="name in targetNames"
  v-if="isTooltip && largeDevice && !wordDoc && !audioDoc"
)
  .hotspot-tooltip.normal-theme.pointer-events-none.bg-white.m-4.rounded-md.p-6.text-center.drop-shadow-md.w-max(
    class="max-w-[32ch]"
    v-for="{ text } in screen.targets[name]"
  )
    uc-text(:k="`${getLightboxId(name)}.text`" :text="text")

template(v-for="name in targetNames")
  template(v-if="screen.targets[name].length === 1")
    uc-lightbox.p-6(
      :id="getLightboxId(name)" 
      :lightboxstyles="screen.targets[name][0]?.lightboxstyles || screen.lightboxstyles"
      :screen="lightboxScreen(name)"
      @close="onLightboxClose(name)()"
    )
      uc-screen(
        :edit-path="editPath + `.targets.${name}.${i}`"
        :nested="true"
        :screen="s"
        v-for="(s, i) in screen.targets[name]"
      )

  template(v-else="")
    uc-lightbox-page(
      :edit-path="editPath + `.targets.${name}`"
      :edit-path-screens="editPath + `.targets.${name}`"
      :id="getLightboxId(name)"
      :screen="{ screens: screen.targets[name] }"
      @close="onLightboxClose(name)()"
      v-if="screen.targets[name].length > 1"
    )
  
  //- add a lightbox teleport target after each target to include the content at the correct place in the docs (HSG creates its own after each level)
  .word-hotspot-lightbox-target(
    :data-teleport="getLightboxId(name)"
    v-if="(wordDoc || audioDoc) && !isHotspotGame"
  )

//- cards for small devices (non-game standalone hotspots)
screen-cards.lg_hidden.screen--no-fade(
  v-if="showMobileCards"
  :editPath="editPath"
  :screen="{ cols: 3, cards: getCardsFromTargets() }"
  uc-word-skip=""
  uc-dont-read=""
)
</template>

<script setup lang="ts">
import { onKeyDown, useEventListener } from '@vueuse/core';
import type { MaybeComputedRef } from '@vueuse/core';
import {
  ref,
  nextTick,
  watch,
  watchEffect,
  computed,
  unref,
  inject,
  reactive,
  onUnmounted
} from 'vue';
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
import { getCompleted, setCompleted } from '../lib/SCORM';
import useThemedSvg from '../lib/useThemedSvg';
import isVisible from '../lib/isVisible';
import openLightbox, { getOpenLightbox } from '../lib/openLightbox';
import gsap from 'gsap';
import { throttle } from 'lodash-es';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { createPopper } from '@popperjs/core';
import useSuspend from '../lib/useSuspend';
import useAudio from '../stores/useAudio';
import { play, stop } from '../lib/audio';
import {
  useChildScreenAudio,
  type NarratableScreen
} from '../lib/useScreenAudio';
import { synthHotspotTargetLightboxShell } from '../lib/screenAudioText';

const wordDoc = inject('wordDoc');
const audioDoc = inject('audioDoc');
const elRef = ref<HTMLSpanElement>();
const props = defineProps<{ screen: ScreenHotspot; editPath: string }>();
const tooltipEls = reactive({});
const activeTooltip = ref<string | null>(null);
const suspend = useSuspend();
const { getAudioURL } = useAudio();
const { resolveChildIntro, audioAiJitEnabled } = useChildScreenAudio();

const setTooltipRef = (el: HTMLElement) => {
  const id = el?.dataset?.tooltipId;
  if (id) tooltipEls[id] = el;

  return el;
};

const svg = useThemedSvg(
  { src: props.screen.hotspot as string },
  { transformClass: true }
);

const sectionId = inject('sectionId') as string;

// emits for the hotspot game
const emit = defineEmits<{
  (e: 'close', data: { name: string; el: HTMLElement }): void;
  (e: 'completed', data: { name: string; el: HTMLElement }): void;
  (e: 'allCompleted', data: { el: HTMLElement }): void;
}>();

const targetNames = computed(() => Object.keys(props.screen.targets));
const id = computed(() => props.screen.id);
let didEmitAllCompleted = false;

const getSuspendKey = (name: string) => `hotspot ${id.value} ${name}`;
const getLightboxId = (name: string) => `lightbox_${id.value}_${name}`;

const lightboxScreen = (name: string): NarratableScreen | undefined => {
  if (!audioAiJitEnabled.value) return undefined;

  const target = props.screen.targets[name]?.[0];
  if (!target || !id.value) return undefined;

  return synthHotspotTargetLightboxShell(id.value, name, target);
};

const getTargetContentId = (name: string) =>
  `lightboxcontent_${getLightboxId(name)}`;
//const getTooltipId = ((name: string) => `tooltip_${id.value}_${name}`);

const breakpoints = useBreakpoints(breakpointsTailwind);
const largeDevice = breakpoints.greaterOrEqual('lg');

const isTooltip = computed(() => {
  if (!props.screen['variant-hotspot']) return false;
  return props.screen['variant-hotspot'] === 'tooltip' ? true : false;
});

const isFullscreenHotspot = computed(() => {
  return props.screen['hotspot-fullscreen'] ? true : false;
});

const hideHotspotBg = computed(() => {
  return props.screen['hideHotspotBg'] ? true : false;
});

const isHotspotGame = computed(() => {
  return props.screen['hotspot-game'] ? true : false;
});

const showMobileCards = computed(
  () => !isHotspotGame.value && !largeDevice.value
);

const getCardsFromTargets = () => {
  const cards: any[] = [];
  Object.keys(props.screen.targets).forEach((name, i) => {
    const target = props.screen.targets[name][0];
    cards.push({
      id: target.id ?? `${id.value}-target${i}`,
      heading: target?.heading?.trim(),
      subheading: target?.subheading?.trim(),
      opens: getLightboxId(name),
      ...target.card
    });
  });
  return cards;
};

const updateCompleted = () => {
  let allCompleted = true;
  let count = 0;

  targetNames.value?.forEach((name: string) => {
    const key = getSuspendKey(name);

    if (getCompleted(key)) {
      const g = elRef.value?.querySelector<SVGGElement>(
        `[data-class="completed"] [data-class~=${name}]`
      );

      if (g) g.style.opacity = '1';

      const target = elRef.value?.querySelector<SVGGElement>(
        `[data-class=targets] > [data-class~=${name}]`
      );
      if (target) target.setAttribute('aria-label', getAriaLabel(name));

      count++;
    } else {
      allCompleted = false;
    }
  });

  if (props.screen.count === 0) {
    allCompleted = true;
  }

  if (props.screen.count && count >= props.screen.count) {
    allCompleted = true;
  }

  if (allCompleted) {
    const el = unref(elRef);
    if (!el) return;

    setCompleted(id.value);

    // FIXME this is a temp solution
    // mark section complete if it is an ancestor of the hotspot, allows unlocking from a split without unlocking from e.g. a hotspot game
    if (
      sectionId === elRef.value?.closest('.section')?.id &&
      !elRef.value?.closest('.screen-hotspot-game')
    ) {
      setCompleted(sectionId);
    }

    if (!didEmitAllCompleted) {
      emit('allCompleted', { el });
      didEmitAllCompleted = true;
    }
  }
};

const getAriaLabel = (name: string) => {
  const screen = props.screen.targets[name][0];
  const targetState = getCompleted(getSuspendKey(name))
    ? 'completed'
    : 'incomplete';
  const targetText =
    screen['hotspot-alt'] || screen.heading || screen.subheading || '';

  // TODO - add robust error messaging that does not interupt workflow
  //if (targetText === '') {
  //  bugAlert(`#${props.screen.id}\n\n${name}\n\nTarget is missing a heading, subheading or hotspot-alt. If the target has no heading or subheading, please add a 'hotspot-alt' field for use with screen readers.`);
  //}

  return `${targetText} ${targetState}`;
};

const isNameNotInContent = (name: string) => {
  if (!(name in props.screen.targets)) {
    console.error(
      `No targets found with name "${name}" at path ${props.editPath}.`
    );

    return true;
  }
};

let hintTimeout: ReturnType<typeof setTimeout>;
let hintTl: gsap.core.Timeline;

// find the first incomplete target and give it a glowing hint
const startHint = () => {
  if (!elRef.value) return;
  if (getOpenTarget()) return;

  let hintTarget: SVGGElement | null = null;
  const targets = elRef.value.querySelectorAll<SVGGElement>(
    '[data-class=targets] > [data-class]'
  );

  for (let i = 0; i < targets.length && !hintTarget; i++) {
    const name = targets[i].dataset.class;

    if (name && !getCompleted(getSuspendKey(name))) {
      hintTarget = targets[i];
    }
  }

  if (!hintTarget) return;
  if (!isVisible(hintTarget)) return;

  endHint();

  hintTl = gsap
    .timeline({ repeat: -1 })
    .to(hintTarget, {
      opacity: 1,
      ease: 'sine.inOut',
      overwrite: true,
      duration: 2
    })
    .to(hintTarget, { opacity: 0, ease: 'sine.inOut', duration: 2 });
};

// remove hint from all targets
const endHint = () => {
  clearTimeout(hintTimeout);

  const targets = elRef.value?.querySelectorAll<SVGGElement>(
    '[data-class=targets] > [data-class]'
  );

  if (hintTl) hintTl?.kill();
  if (targets) gsap.to(targets, { opacity: 0, overwrite: true });
};

// start timer to apply glowing hint, save for cancelling
const startHintTimer = () => {
  clearTimeout(hintTimeout);
  hintTimeout = setTimeout(startHint, 1e3);
};

// define empty function immediately that we will fill later, to simplify attaching the event listener
let updateParallax: (e: MouseEvent) => void;

const getParallaxValue = (target: SVGElement) =>
  Number(target.dataset.class?.match(/ax-(-*\d+)/)?.[1]);

let initializedOnce = false;

const onResize = () => {
  const el = unref(elRef);

  if (!el) return;

  const s = el.querySelector<SVGElement>('svg');
  const t = el.querySelector<SVGElement>('svg [data-class="targets"]') || s;

  if (!s || !t) return;

  s.style.transform = 'none';
  s.style.transformOrigin = '0 0';

  const { width: w1, height: h1, x: x1, y: y1 } = el.getBoundingClientRect();
  const { width: w2, height: h2 } = s.getBoundingClientRect();
  const { width: w3, height: h3, x: x3, y: y3 } = t.getBoundingClientRect();

  /**
   * default is to fit the width or height
   * fullscreen hotspot scales to fit fully in the window below large, and to fill the window above large
   * hotspot game always scales to fit fully in the window
   **/
  let scale = Math.max(w1 / w2, h1 / h2);

  if (isFullscreenHotspot.value) {
    scale = largeDevice.value
      ? Math.max(w1 / w2, h1 / h2)
      : Math.min(w1 / w2, h1 / h2);
  } else if (isHotspotGame.value) {
    scale = largeDevice.value ? Math.min(w1 / w2, h1 / h2) : w1 / w2;
  }

  // parallax bleed, if needed
  if (el.querySelector('[data-class*="parallax-"]')) {
    const pX = 50;
    scale *= (scale * w2 + pX) / (scale * w2);
  }

  let x = 0;
  let y = 0;

  /**
   * offset x and y based on the hotspot type and the device size:
   * - fullscreen hotspots are centered on smaller devices and cropped/aligned to keep the hotspot targets in view on large devices
   * - hotspot games fit the width or height are centered
   **/
  if (
    (isFullscreenHotspot.value && !largeDevice.value) ||
    isHotspotGame.value
  ) {
    x = (window.innerWidth - w2 * scale) / 2;
    y = (window.innerHeight - h2 * scale) / 2;
  } else if (isFullscreenHotspot.value && largeDevice.value) {
    // crop svg but make sure hotspots are in window
    const targetRightEdge = x1 + scale * (x3 - x1 + w3);
    const hotspotRightEdge = x1 + w1;

    const targetBottomEdge = y1 + scale * (y3 - y1 + h3);
    const hotspotBottomEdge = y1 + h1;

    if (targetRightEdge > hotspotRightEdge) {
      x = hotspotRightEdge - targetRightEdge;
    }

    if (targetBottomEdge > hotspotBottomEdge) {
      y = hotspotBottomEdge - targetBottomEdge;
    }
  }

  s.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
};

// when svg is ready:
// - apply a11y to targets
// - update completed states
// - start the first hint timeout
// - init parallax
watchEffect(
  async function initSVG() {
    const el = unref(elRef);
    if (!el) return;
    const targets = el.querySelectorAll('[data-class*="parallax-"]');

    if (initializedOnce) return;
    if (!svg.value) return;
    if (!el) return;

    initializedOnce = true;

    el.querySelectorAll('[data-class=targets] > g').forEach(
      (target: SVGGElement) => {
        const name = target.dataset.class;

        if (!name) return;

        if (isNameNotInContent(name)) {
          target.style.display = 'none';
          return;
        }

        target.setAttribute('tabindex', '0');
        target.setAttribute('role', 'button');
        target.setAttribute('aria-label', getAriaLabel(name));

        const onEnter = () => {
          endHint();

          window.requestAnimationFrame(() => {
            gsap.to(target, {
              opacity: 1,
              overwrite: true,
              duration: 0.15,
              ease: 'linear'
            });
          });
        };

        const onLeave = () => {
          gsap.to(target, { opacity: 0, overwrite: true, duration: 0.3 });
          startHintTimer();
        };

        target.addEventListener('mouseenter', onEnter);
        target.addEventListener('focus', onEnter);

        target.addEventListener('mouseleave', onLeave);
        target.addEventListener('blur', onLeave);
      }
    );

    updateParallax = throttle((e: MouseEvent) => {
      // FIXME consistent with window size
      const x = e.clientX / window.innerWidth - 0.5;
      const y = e.clientY / window.innerHeight - 0.5;

      const getX = (target: SVGElement) => {
        const p = getParallaxValue(target);
        const r = p * x * -0.5;

        return r;
      };

      gsap.to(targets, {
        x: (_, target: SVGElement) => getX(target),
        y: (_, target: SVGElement) => getParallaxValue(target) * y * -0.1,

        duration: 2
      });
    }, 100);

    onResize();
    await nextTick();
    updateCompleted();
    startHintTimer();
  },
  { flush: 'post' }
);

// use watchEffect to listen for changes to each targets suspend value getSuspendKey(name) and reset the target element style.opacity to 0
watchEffect(() => {
  targetNames.value?.forEach((name: string) => {
    const key = getSuspendKey(name);
    const completed = getCompleted(key);
    if (!completed) {
      const target = elRef.value?.querySelector<SVGGElement>(
        `[data-class="completed"] [data-class~=${name}]`
      );
      if (target) target.style.opacity = '0';
    }
  });
});

const onTargetClick = async (e: MouseEvent | KeyboardEvent) => {
  const el = e.target as SVGElement;
  const target = el.closest('[data-class=targets] > g') as SVGGElement;

  if (target) {
    const name = target.dataset.class;

    if (!name) return;
    if (isNameNotInContent(name)) return;

    e.preventDefault();
    endHint();

    if (isTooltip.value && largeDevice.value) {
      showTooltip(target, name);
    } else {
      openLightbox(getLightboxId(name));
    }
  }
};

useEventListener(elRef, 'mouseenter', startHintTimer);
useEventListener(elRef, 'click', onTargetClick);
onKeyDown([' ', 'Enter'], onTargetClick, {
  target: elRef as MaybeComputedRef<EventTarget>
});

// TOOLTIP
async function showTooltip(target: SVGGElement, name: string) {
  const id = getLightboxId(name);
  if (id !== activeTooltip.value) hideTooltip();

  const rootBoundary = tooltipEls[id].querySelector('.uc-hotspot');
  const boundary = tooltipEls[id].querySelector(`[data-class="${name}"]`);
  await createPopper(target, tooltipEls[id], {
    placement: 'top',
    modifiers: [
      {
        name: 'preventOverflow',
        options: {
          mainAxis: false,
          boundary: boundary,
          rootBoundary: rootBoundary
        }
      }
    ]
  });

  // animate tool tip
  animIn(tooltipEls[id]);
  activeTooltip.value = id;

  const el = unref(elRef);

  if (!el) return;

  setCompleted(getSuspendKey(name));
  await nextTick();
  // play audio for simple lightbox
  if (isTooltip.value && props.screen.targets[name].length === 1) {
    const tooltipAudio = await getTooltipAudio(name);
    if (tooltipAudio) {
      await stop({ fadeOut: 0.2 });
      play(tooltipAudio, { owner: getLightboxId(name) });
    }
  }

  emit('completed', { name, el });
  updateCompleted();
}

function hideTooltip() {
  if (tooltipEls && activeTooltip.value)
    animOut(tooltipEls[activeTooltip.value]);
  activeTooltip.value = null;
}

const getTooltipAudio = async (name: string) => {
  const screen = lightboxScreen(name);
  const audioKey = `lightbox-${getLightboxId(name)}`;

  if (audioAiJitEnabled.value && screen) {
    return resolveChildIntro(screen, { audioKey });
  }

  return getAudioURL(audioKey) || null;
};

function animIn(tooltipEl: HTMLElement) {
  gsap
    .timeline({
      paused: true,
      onComplete: () => {
        focusTooltip(tooltipEl);
      }
    })
    .fromTo(
      tooltipEl,
      { opacity: 0 },
      { opacity: 1, duration: 0.2, ease: 'easeOut' }
    )
    .play();
}

function animOut(tooltipEl: HTMLElement) {
  gsap
    .timeline({
      paused: true,
      onComplete: () => {
        stop({ fadeOut: 0.2 });
      }
    })
    .fromTo(
      tooltipEl,
      { scale: 1, opacity: 1 },
      { scale: 0.9, opacity: 0, duration: 0.1, ease: 'easeOut' }
    )
    .play();
}

async function focusTooltip(el: HTMLElement) {
  if (!activeTooltip.value) return;
  document.getElementById(activeTooltip.value)?.focus();

  el.addEventListener('blur', () => {
    activeTooltip.value = null;
    animOut(el);
  });
}

const onLightboxClose = (name: string) => async () => {
  const el = unref(elRef);
  if (!el) return;

  const targetComplete = getCompleted(getTargetContentId(name));
  const lightboxComplete = getCompleted(getLightboxId(name));

  if (targetComplete && lightboxComplete) {
    setCompleted(getSuspendKey(name));
    await nextTick();
    emit('completed', { name, el });
    updateCompleted();
  }

  emit('close', { name, el });

  setTimeout(() => {
    endHint();
    startHintTimer();
  }, 200);
};

function getOpenTarget() {
  if (!isTooltip.value) return getOpenLightbox();
  return (
    elRef.value?.querySelector<HTMLElement>('[data-tooltip-id]') || undefined
  );
}

useEventListener(elRef, 'mousemove', (e: MouseEvent) => {
  if (updateParallax && !suspend.reducedMotion && largeDevice.value)
    updateParallax(e);
});

const throttledResize = throttle(onResize, 100, { leading: false });

ScrollTrigger.addEventListener('refresh', throttledResize);
useEventListener(window, 'resize', throttledResize);

watch(
  largeDevice,
  async isLarge => {
    onResize();
    await nextTick();
    if (isLarge && elRef.value) {
      elRef.value.classList.add('-in');
    }
  },
  { flush: 'post' }
);

onUnmounted(() => {
  ScrollTrigger.removeEventListener('refresh', throttledResize);
});
</script>

<style>
.uc-hotspot-bg svg {
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
}

.uc-hotspot svg {
  width: 100%;
  height: auto;
  top: 0;
  left: 0;
}
</style>

<style scoped>
:deep([data-class='targets'] > g),
:deep([data-class='completed'] > g) {
  @apply opacity-0 transition-opacity;
}

:deep([data-class='targets'] > g) {
  @apply cursor-pointer !outline-none;

  &:focus-visible {
    outline: rgb(var(--color-sky-700)) solid 8px !important;
  }

  &:focus-visible:where(.dark *) {
    outline: rgb(var(--color-sky-400)) solid 8px !important;
  }
}

/* hide target/completed layers in the decorative background SVG */
:deep(.uc-hotspot-bg [data-class='targets']),
:deep(.uc-hotspot-bg [data-class='completed']) {
  display: none;
}
</style>
